import { useEffect, useRef, useState, type ReactNode } from 'react';
import { type PagedResult, type QueryOptions } from '../lib/types';

export interface Column<T> {
  /** Column header label. */
  header: string;
  /** Optional sort key — when set, the column header is clickable and emits onOptionsChange with `sortBy`. */
  sortBy?: string;
  /** Cell renderer for a row. */
  render: (row: T) => ReactNode;
  /** Optional extra `<td>` class names. */
  cellClass?: string;
  /** Optional extra `<th>` class names. */
  headerClass?: string;
}

interface DataTableProps<T> {
  data: PagedResult<T> | undefined;
  isLoading: boolean;
  error: unknown;
  options: QueryOptions;
  onOptionsChange: (next: QueryOptions) => void;
  columns: Column<T>[];
  /** Stable key for each row — usually `r => r.id`. */
  rowKey: (row: T) => string | number;
  /** Optional row click handler (highlights row + cursor:pointer). */
  onRowClick?: (row: T) => void;
  /** Optional empty-state content. Defaults to "No results.". */
  emptyState?: ReactNode;
  /**
   * When true, renders a debounced search input above the table that emits
   * `options.Filter`. The server-side repository interprets the filter (typically a free-text
   * substring match across selected columns).
   */
  searchable?: boolean;
  /** Placeholder for the search input. Defaults to "Search…". */
  searchPlaceholder?: string;
  /**
   * Optional toolbar slot for export buttons, "New" buttons, etc. Rendered to the right of
   * the search input.
   */
  toolbar?: ReactNode;
}

/**
 * Generic paginated table. Consumes the API's <code>PagedResult&lt;T&gt;</code> shape and
 * emits <code>QueryOptions</code> changes back to the parent so it can refetch via TanStack
 * Query. Use with any list endpoint that returns <code>PagedResult&lt;T&gt;</code>.
 *
 * Renders three states: loading (skeleton), error (banner), data (rows or empty state).
 */
export function DataTable<T>({
  data,
  isLoading,
  error,
  options,
  onOptionsChange,
  columns,
  rowKey,
  onRowClick,
  emptyState,
  searchable,
  searchPlaceholder = 'Search…',
  toolbar,
}: DataTableProps<T>) {
  if (error) {
    return (
      <div role="alert" className="bg-red-50 border border-red-200 text-red-900 text-sm rounded px-3 py-2">
        Failed to load. {error instanceof Error ? error.message : ''}
      </div>
    );
  }

  return (
    <div className="border rounded-md bg-white">
      {searchable || toolbar ? (
        <div className="flex items-center gap-3 px-3 py-2 border-b border-slate-200">
          {searchable ? (
            <SearchInput
              value={options.filter ?? ''}
              placeholder={searchPlaceholder}
              onChange={(next) => onOptionsChange({ ...options, filter: next, page: 1 })}
            />
          ) : <span className="flex-1" />}
          {toolbar}
        </div>
      ) : null}
      <table className="w-full text-sm">
        <thead className="text-left text-slate-500 border-b">
          <tr>
            {columns.map((c) => (
              <th key={c.header} className={`py-2 px-3 font-medium ${c.headerClass ?? ''}`}>
                {c.sortBy ? (
                  <button
                    type="button"
                    onClick={() => handleSort(c.sortBy!, options, onOptionsChange)}
                    className="inline-flex items-center gap-1 hover:text-slate-800"
                  >
                    {c.header}
                    {sortIndicator(c.sortBy, options)}
                  </button>
                ) : (
                  c.header
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading && !data ? (
            <SkeletonRows columns={columns.length} />
          ) : data && data.items.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="py-6 px-3 text-center text-slate-500">
                {emptyState ?? 'No results.'}
              </td>
            </tr>
          ) : (
            data?.items.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={() => onRowClick?.(row)}
                className={`border-b last:border-0 ${onRowClick ? 'cursor-pointer hover:bg-slate-50' : ''}`}
              >
                {columns.map((c) => (
                  <td key={c.header} className={`py-2 px-3 ${c.cellClass ?? ''}`}>
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
      {data ? (
        <Pagination
          data={data}
          options={options}
          onOptionsChange={onOptionsChange}
        />
      ) : null}
    </div>
  );
}

function handleSort(
  field: string,
  options: QueryOptions,
  onOptionsChange: (next: QueryOptions) => void
) {
  const same = options.sortBy === field;
  onOptionsChange({
    ...options,
    sortBy: field,
    sortDesc: same ? !options.sortDesc : false,
    page: 1
  });
}

function SearchInput({
  value,
  placeholder,
  onChange,
}: {
  value: string;
  placeholder: string;
  onChange: (next: string) => void;
}) {
  // Debounce keystrokes so we don't refetch on every character — 300ms is the standard
  // "feels instant but doesn't hammer the server" window.
  const [local, setLocal] = useState(value);
  const isFirstRender = useRef(true);

  useEffect(() => {
    setLocal(value);
  }, [value]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const t = window.setTimeout(() => {
      if (local !== value) onChange(local);
    }, 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local]);

  return (
    <div className="flex-1 max-w-sm">
      <input
        type="search"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        placeholder={placeholder}
        className="w-full border border-slate-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-atg-blue/30"
      />
    </div>
  );
}

function sortIndicator(field: string, options: QueryOptions) {
  if (options.sortBy !== field) return <span className="text-slate-300">⇅</span>;
  return <span>{options.sortDesc ? '↓' : '↑'}</span>;
}

function SkeletonRows({ columns }: { columns: number }) {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <tr key={i} className="border-b last:border-0 animate-pulse">
          {Array.from({ length: columns }).map((__, j) => (
            <td key={j} className="py-3 px-3"><div className="h-3 bg-slate-200 rounded" /></td>
          ))}
        </tr>
      ))}
    </>
  );
}

function Pagination<T>({
  data,
  options,
  onOptionsChange,
}: {
  data: PagedResult<T>;
  options: QueryOptions;
  onOptionsChange: (next: QueryOptions) => void;
}) {
  const page = data.page;
  const totalPages = data.totalPages;
  const goto = (p: number) => onOptionsChange({ ...options, page: p });

  return (
    <div className="flex items-center justify-between px-3 py-2 text-xs text-slate-600 border-t">
      <span>
        {data.totalCount === 0
          ? 'No items'
          : `${(page - 1) * data.pageSize + 1}–${Math.min(page * data.pageSize, data.totalCount)} of ${data.totalCount}`}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => goto(Math.max(1, page - 1))}
          disabled={page <= 1}
          className="px-2 py-1 rounded border border-slate-300 disabled:opacity-40 hover:bg-slate-100"
        >
          Prev
        </button>
        <span>
          Page {page} of {Math.max(1, totalPages)}
        </span>
        <button
          type="button"
          onClick={() => goto(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          className="px-2 py-1 rounded border border-slate-300 disabled:opacity-40 hover:bg-slate-100"
        >
          Next
        </button>
      </div>
    </div>
  );
}
